import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

const formatPercent = (value) => {
  if (value === null || value === undefined) return 'N/A';
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
};

function scoreColor(v) {
  if (v >= 60) return '#059669';
  if (v >= 50) return '#3b82f6';
  if (v >= 40) return '#d97706';
  return '#dc2626';
}

/* ── Mini score bar ── */
const ScoreBar = ({ value, color, width = 48 }) => (
  <Box sx={{ width, height: 5, borderRadius: 3, bgcolor: `${color}15`, mt: 0.5 }}>
    <Box sx={{ width: `${Math.min(value, 100)}%`, height: '100%', bgcolor: color, borderRadius: 3, transition: 'width .4s' }} />
  </Box>
);

const SummaryCards = ({
  averageScore,
  minScore,
  maxScore,
  sortedScores,
  totalIndicators,
  topPerformer,
  currentLang,
  hotRunsCount,
  hotRunStats,
  overboughtCount,
  overboughtStats
}) => {
  const { t } = useTranslation();
  const avgColor = scoreColor(averageScore);
  const topScore = topPerformer?.combined_score ?? topPerformer?.impulse_score_0_100 ?? 0;
  const topColor = scoreColor(topScore);
  const topName = topPerformer?.sector_name
    ? (currentLang === 'vi' ? topPerformer.sector_name.vi : topPerformer.sector_name.en)
    : 'N/A';

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
      gap: { xs: 1.5, md: 2 },
      mb: { xs: 2, md: 2.5 },
    }}>
      {/* Average Score */}
      <Box sx={{
        p: 2, borderRadius: 2.5, border: '1px solid', borderColor: 'divider',
        bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', gap: 0.5,
      }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{
          textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.63rem',
        }}>
          {t('sectorMomentum.cards.averageScore', 'Điểm TB')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography sx={{
            fontSize: '1.6rem', fontWeight: 800, color: avgColor, lineHeight: 1,
            fontFeatureSettings: '"tnum"',
          }}>
            {averageScore.toFixed(1)}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.68rem' }}>
            / 100
          </Typography>
        </Box>
        <ScoreBar value={averageScore} color={avgColor} />
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem', mt: 0.25 }}>
          {t('sectorMomentum.cards.range', 'Biên')} {minScore.toFixed(0)} – {maxScore.toFixed(0)} · {sortedScores.length} {t('sectorMomentum.table.sector', 'ngành')}
        </Typography>
      </Box>

      {/* Top Performer */}
      <Box sx={{
        p: 2, borderRadius: 2.5, border: '1px solid', borderColor: 'divider',
        bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', gap: 0.5,
      }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{
          textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.63rem',
        }}>
          {t('sectorMomentum.cards.topPerformer', 'Dẫn Đầu')}
        </Typography>
        <Typography sx={{
          fontSize: '0.85rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.3,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {topName}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography sx={{
            fontSize: '1.4rem', fontWeight: 800, color: topColor, lineHeight: 1,
            fontFeatureSettings: '"tnum"',
          }}>
            {topScore.toFixed(1)}
          </Typography>
          {topPerformer?.price_momentum?.ret_5d_pct != null && (
            <Typography variant="caption" sx={{
              fontWeight: 600, fontSize: '0.7rem',
              color: topPerformer.price_momentum.ret_5d_pct >= 0 ? '#059669' : '#dc2626',
            }}>
              {formatPercent(topPerformer.price_momentum.ret_5d_pct)} 5d
            </Typography>
          )}
        </Box>
        <ScoreBar value={topScore} color={topColor} />
      </Box>

      {/* Hot Runs */}
      <Box sx={{
        p: 2, borderRadius: 2.5, border: '1px solid', borderColor: hotRunsCount > 0 ? '#ef444425' : 'divider',
        bgcolor: hotRunsCount > 0 ? '#fef2f2' : 'background.paper',
        display: 'flex', flexDirection: 'column', gap: 0.5,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ef4444', flexShrink: 0 }} />
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{
            textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.63rem',
          }}>
            Hot Runs
          </Typography>
        </Box>
        <Typography sx={{
          fontSize: '1.6rem', fontWeight: 800, color: hotRunsCount > 0 ? '#dc2626' : 'text.disabled',
          lineHeight: 1, fontFeatureSettings: '"tnum"',
        }}>
          {hotRunsCount}
          <Typography component="span" sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.disabled', ml: 0.5 }}>
            / {sortedScores.length}
          </Typography>
        </Typography>
        {hotRunsCount > 0 && (
          <Box sx={{ display: 'flex', gap: 1.5, mt: 0.25 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              {t('sectorMomentum.cards.avgScore', 'TB')}: <b>{hotRunStats.avgScore.toFixed(0)}</b>
            </Typography>
            <Typography variant="caption" sx={{
              fontSize: '0.65rem', fontWeight: 600,
              color: hotRunStats.avgReturn >= 0 ? '#059669' : '#dc2626',
            }}>
              5d: {formatPercent(hotRunStats.avgReturn)}
            </Typography>
          </Box>
        )}
        {hotRunsCount === 0 && (
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem', fontStyle: 'italic' }}>
            {t('sectorMomentum.cards.noSectors', 'Không có ngành nào')}
          </Typography>
        )}
      </Box>

      {/* Overbought */}
      <Box sx={{
        p: 2, borderRadius: 2.5, border: '1px solid', borderColor: overboughtCount > 0 ? '#f59e0b25' : 'divider',
        bgcolor: overboughtCount > 0 ? '#fffbeb' : 'background.paper',
        display: 'flex', flexDirection: 'column', gap: 0.5,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#f59e0b', flexShrink: 0 }} />
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{
            textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.63rem',
          }}>
            {t('sectorMomentum.cards.overbought', 'Quá Mua')}
          </Typography>
        </Box>
        <Typography sx={{
          fontSize: '1.6rem', fontWeight: 800, color: overboughtCount > 0 ? '#d97706' : 'text.disabled',
          lineHeight: 1, fontFeatureSettings: '"tnum"',
        }}>
          {overboughtCount}
          <Typography component="span" sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.disabled', ml: 0.5 }}>
            / {sortedScores.length}
          </Typography>
        </Typography>
        {overboughtCount > 0 && (
          <Box sx={{ display: 'flex', gap: 1.5, mt: 0.25 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              {t('sectorMomentum.cards.avgScore', 'TB')}: <b>{overboughtStats.avgScore.toFixed(0)}</b>
            </Typography>
            <Typography variant="caption" sx={{
              fontSize: '0.65rem', fontWeight: 600,
              color: overboughtStats.avgReturn >= 0 ? '#059669' : '#dc2626',
            }}>
              5d: {formatPercent(overboughtStats.avgReturn)}
            </Typography>
          </Box>
        )}
        {overboughtCount === 0 && (
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem', fontStyle: 'italic' }}>
            {t('sectorMomentum.cards.noSectors', 'Không có ngành nào')}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default SummaryCards;
